/**
 * PreCallBriefCard — 4-line concierge brief surfaced on the Client Profile
 * whenever the client has a scheduled consultation. Quiet, editorial,
 * never auto-loaded — advisor opens it on demand to keep AI usage tight.
 */
import React from "react";
import { Sparkles, Loader2, RefreshCw } from "lucide-react";

import { Surface, Heading, PortalButton, api } from "../../../index";

function formatBrief(text) {
  if (!text) return null;
  return text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, i) => {
      const m = line.match(/^([A-Z][a-zA-Z ]+):\s*(.+)$/);
      if (m) {
        return { key: i, label: m[1], body: m[2] };
      }
      return { key: i, label: null, body: line };
    });
}

export default function PreCallBriefCard({ client, clientId, onUpdated }) {
  const [brief, setBrief] = React.useState(client?.precall_brief || null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    setBrief(client?.precall_brief || null);
  }, [client?.precall_brief]);

  const generate = async (regenerate = false) => {
    setBusy(true);
    setErr(null);
    try {
      const { data } = await api.get(`/clients/${clientId}/precall-brief`, {
        params: regenerate ? { regenerate: true } : {},
      });
      setBrief(data.brief);
      onUpdated?.(data.brief);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't generate brief.");
    } finally {
      setBusy(false);
    }
  };

  const lines = formatBrief(brief);

  return (
    <Surface variant="paper" padded data-testid="precall-brief-card">
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
          <Heading level="eyebrow">Pre-call brief</Heading>
        </div>
        <div className="flex items-center gap-2">
          {brief && (
            <PortalButton
              variant="ghost"
              size="sm"
              onClick={() => generate(true)}
              disabled={busy}
              icon={busy ? <Loader2 className="size-3 animate-spin" /> : <RefreshCw className="size-3" strokeWidth={1.5} />}
              data-testid="precall-brief-refresh"
            >
              Regenerate
            </PortalButton>
          )}
          {!brief && (
            <PortalButton
              variant="primary"
              size="sm"
              onClick={() => generate(false)}
              disabled={busy}
              icon={busy ? <Loader2 className="size-3 animate-spin" /> : <Sparkles className="size-3" strokeWidth={1.5} />}
              data-testid="precall-brief-generate"
            >
              Generate
            </PortalButton>
          )}
        </div>
      </div>
      <p className="mt-0 font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))] mb-5">
        A four-line concierge orientation, written from limited intake.
        For your eyes only.
      </p>
      {err && (
        <p className="font-accent italic text-[13px] text-[hsl(var(--accent-terracotta))]" data-testid="precall-brief-error">
          {err}
        </p>
      )}
      {!brief && !err && !busy && (
        <p className="font-body text-[13px] text-[hsl(var(--foreground-muted))]">
          Tap Generate to prepare for this consultation.
        </p>
      )}
      {brief && (
        <ul className="space-y-3" data-testid="precall-brief-body">
          {lines.map((row) => (
            <li key={row.key} className="grid grid-cols-[120px_1fr] gap-x-4 items-baseline">
              {row.label && (
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                  {row.label}
                </span>
              )}
              <span className="font-body text-[14px] leading-snug text-[hsl(var(--foreground))]">
                {row.body}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Surface>
  );
}
